import { IPrintMailStatus } from './types'

/**
 * Whether this tenant is posting real mail, and at whose expense.
 *
 * The worst failure this feature has is a tenant spending real postage while believing they are in
 * a sandbox, so live-versus-test is a band across the top of the screen rather than a field
 * somebody has to go looking for. The reverse case matters too and is just as loud: a test key
 * looks like it is working - pieces move to `submitted` and get a provider id - and nothing ever
 * arrives at a borrower.
 *
 * No credential at all is the expected state before a tenant has signed up with the provider. It
 * reads as "not set up yet" and not as an error, because nothing is broken. It does stop more than
 * posting, though: the API refuses to queue a piece at all without a credential (410), so the band
 * says what still works rather than implying the queue does.
 */
export const PrintMailStatusBanner = ({
  status,
  className = '',
}: {
  status: IPrintMailStatus | null
  className?: string
}) => {
  if (!status) return null

  const band = !status.enabled
    ? {
        cls: 'bg-slate-100 text-slate-700 border-slate-200',
        title: 'Print and mail is switched off for this company.',
        detail: 'Nothing can be queued and nothing is posted. Turn it on under Admin > Configuration > Print & Mail.',
      }
    : !status.configured
    ? {
        cls: 'bg-sky-50 text-sky-900 border-sky-200',
        title: `On, but no ${status.provider || 'provider'} credential has been saved yet.`,
        detail:
          'Costs and the year-end preview work, so a mailing can be priced and reviewed now. Nothing can be ' +
          'queued or posted until a credential is saved under Admin Tools > Vendors.',
      }
    : status.live
    ? {
        cls: 'bg-red-50 text-red-900 border-red-300',
        title: 'LIVE - letters queued here are printed and posted, and postage is charged for real.',
        detail: `Provider: ${status.provider}. A piece can be cancelled for free while it is queued; once the ` +
          'provider has printed it, it is on its way.',
      }
    : {
        cls: 'bg-amber-50 text-amber-900 border-amber-300',
        title: 'TEST key - nothing reaches a borrower.',
        detail: `The ${status.provider} credential in use is a sandbox key. Pieces will move through submitted and ` +
          'mailed and will look posted, but no envelope exists. Swap in a live key before a real mailing.',
      }

  return (
    <div className={`rounded border px-3 py-2.5 text-sm ${band.cls} ${className}`}>
      <p className="font-semibold">{band.title}</p>
      <p className="mt-0.5 text-[13px] opacity-90">{band.detail}</p>
      {!!status.missing?.length && (
        <>
          <p className="mt-2 text-[13px] font-medium">Still missing:</p>
          <ul className="list-disc ml-5 text-[13px]">
            {status.missing.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </>
      )}
    </div>
  )
}
